// Klienti a kontakty: typované funkce nad api() podle smlouvy v docs/SPEC.md část 5.
import type {
  AiNoteResponse,
  ApiList,
  AresCompany,
  ClientConflictDetails,
  ClientDetail,
  ClientSummary,
  ClientTag,
  Contact,
  ContactInput,
  CreateClientInput,
  CreateEntityNoteInput,
  ImportClientsInput,
  ImportClientsResult,
  TimelineEntry,
  UpdateClientInput,
} from '@wac-buddy/shared'
import { api, ApiClientError } from './client'
import { buildQuery } from './query'

export type { ClientDetail }

export interface ListClientsParams {
  q?: string
  sort?: 'activity' | 'name'
  tag?: ClientTag
  cursor?: string | null
  limit?: number
}

export type ClientCandidate = ClientConflictDetails['candidates'][number]

/** Možné duplicity z odpovědi 409 `conflict`, jinak null. */
export function conflictCandidates(error: unknown): ClientCandidate[] | null {
  if (!(error instanceof ApiClientError) || error.code !== 'conflict') return null
  const details = error.details as Partial<ClientConflictDetails> | undefined
  return Array.isArray(details?.candidates) ? details.candidates : null
}

export function listClients(params: ListClientsParams = {}): Promise<ApiList<ClientSummary>> {
  return api(
    `/clients${buildQuery({
      q: params.q?.trim(),
      sort: params.sort,
      tag: params.tag,
      cursor: params.cursor,
      limit: params.limit,
    })}`,
  )
}

export function createClient(input: CreateClientInput): Promise<ClientDetail> {
  return api('/clients', { method: 'POST', body: JSON.stringify(input) })
}

export function getClient(id: string): Promise<ClientDetail> {
  return api(`/clients/${encodeURIComponent(id)}`)
}

export function updateClient(id: string, input: UpdateClientInput): Promise<ClientDetail> {
  return api(`/clients/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) })
}

export function deleteClient(id: string): Promise<void> {
  return api(`/clients/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function getClientTimeline(id: string): Promise<ApiList<TimelineEntry>> {
  return api(`/clients/${encodeURIComponent(id)}/timeline`)
}

export function addContact(clientId: string, input: ContactInput): Promise<Contact> {
  return api(`/clients/${encodeURIComponent(clientId)}/contacts`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateContact(id: string, input: Partial<ContactInput>): Promise<Contact> {
  return api(`/contacts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function deleteContact(id: string): Promise<void> {
  return api(`/contacts/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function importClients(input: ImportClientsInput): Promise<ImportClientsResult> {
  return api('/clients/import', { method: 'POST', body: JSON.stringify(input) })
}

export function lookupAres(ico: string): Promise<AresCompany> {
  return api(`/ares/${encodeURIComponent(ico)}`)
}

/** Poznámka ke klientovi: uloží se do časové osy a Buddy v ní hledá sliby a úkoly. */
export function postClientNote(id: string, input: CreateEntityNoteInput): Promise<AiNoteResponse> {
  return api(`/clients/${encodeURIComponent(id)}/notes`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}
